import { IDENTITY, identityApi, refreshAccessToken, SESSION_HEADER } from '@shared/api';
import { hasAuthorizationPayload } from '@shared/lib/authorization';
import type { ApiResponse } from '@shared/types/api';
import type { AuthSession, AuthUser, LoginCredentials } from '@shared/types/auth';

/** `ChangePasswordDto`: currentPassword, newPassword. */
export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

/**
 * Auth service — the only place the HTTP details of authentication live.
 * Everything goes to identity-service, the platform's single authentication
 * authority; no application has a login of its own.
 *
 * Contract (identity-service `AuthController`):
 * - `POST /auth/login` `{ email, password }` → `{ accessToken, sessionId, user }`
 *   and sets the HttpOnly `refresh_token_<sessionId>` cookie.
 * - `POST /auth/refresh` (cookie; `X-Session-Id` names the session) → same shape.
 * - `POST /auth/logout` (Bearer) revokes the current session only.
 * - `POST /auth/logout-all` revokes every session of this user.
 * - `GET /auth/me` → `AuthUserSummary` (roles, isOwner, applications, roleLevel,
 *   permissions, employeeGrantablePermissions, scopes, employee).
 * - `PATCH /auth/change-password` revokes all sessions.
 */
export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthSession> {
    const { data } = await identityApi.post<ApiResponse<AuthSession>>(
      IDENTITY.auth.login,
      credentials,
    );
    return data.data;
  },

  /** Revoke one session. Other accounts signed in on this browser stay signed in. */
  async logout(sessionId?: string | null): Promise<void> {
    await identityApi.post(IDENTITY.auth.logout, undefined, {
      headers: sessionId ? { [SESSION_HEADER]: sessionId } : undefined,
    });
  },

  async getCurrentUser(): Promise<AuthUser> {
    const { data } = await identityApi.get<ApiResponse<AuthUser>>(IDENTITY.auth.me);
    return data.data;
  },

  /**
   * The session user, guaranteed to carry authorization data. identity's
   * login/refresh already return the full `AuthUserSummary`; `/auth/me` is the
   * fallback for a payload without it.
   */
  async resolveAuthorizedUser(session: AuthSession): Promise<AuthUser> {
    if (hasAuthorizationPayload(session.user)) return session.user;
    return this.getCurrentUser();
  },

  /** Rotate one session (coordinated across tabs; see `refreshAccessToken`). */
  refresh(sessionId?: string | null): Promise<AuthSession> {
    return refreshAccessToken(sessionId ?? null);
  },

  async changePassword(payload: ChangePasswordPayload): Promise<void> {
    await identityApi.patch(IDENTITY.auth.changePassword, payload);
  },

  async logoutAll(): Promise<void> {
    await identityApi.post(IDENTITY.auth.logoutAll);
  },
} as const;
